import { Controller } from '@hotwired/stimulus';
import { rewriteRaptorTeaserLink } from '../modules/raptorUsTeaserLink';

export default class extends Controller {
    static targets = [
        'header',
        'input',
        'closeButton',
        'panel',
        'suggestionsSection',
        'suggestionsList',
        'popularSection',
        'productsSection',
        'popularList',
        'productsWrapper',
        'productsSlider',
        'showAllLink',
        'searchMenuItem',
    ];

    static values = {
        raptorAutocompleteEndpoint: String,
        raptorApiKey: String,
        raptorProductIndex: String,
        raptorProductQueryBy: String,
        raptorBlogIndex: String,
        raptorNewsIndex: String,
        raptorCategoriesIndex: String,
        raptorPagesIndex: String,
        raptorMarketFilter: String,
        usUrlPrefix: String,
        raptorIndexes: String,
        productCtaLabel: String,
        productsSearchPageUrl: String,
        searchPageUrl: String,
    };

    connect() {
        this.debounceTimer = null;
        this.currentQuery = '';
        this.suggestionsLoaded = false;

        this.didScroll = false;
        this.lastScrollTop = 0;
        this.isSuspended = false;
        this.delta = 5;

        this.handleScroll = this.handleScroll.bind(this);
        this.handleClickOutside = this.handleClickOutside.bind(this);

        window.addEventListener('scroll', this.handleScroll);
        this.scrollCheck();

        document.addEventListener('mousedown', this.handleClickOutside);

        if (this.hasPanelTarget) {
            this.panelTarget.classList.remove('is-open');
        }

        this.#showSuggestionsOnly();
        this.fetchInitialSuggestions();
    }

    disconnect() {
        document.removeEventListener('mousedown', this.handleClickOutside);
    }

    scrollCheck() {
        if (this.didScroll && !this.isSuspended) {
            this.#hasScrolled();
            this.didScroll = false;
        }

        requestAnimationFrame(this.scrollCheck.bind(this));
    }

    handleScroll() {
        this.didScroll = true;
    }

    suspend() {
        this.isSuspended = true;
    }

    resume() {
        this.lastScrollTop = window.scrollY || 0;
        this.isSuspended = false;
    }

    onInput(event) {
        const query = (event.target.value || '').trim();
        this.currentQuery = query;

        if (!this.hasPanelTarget) {
            return;
        }

        if (!query.length) {
            this.clearResults();
            this.#showSuggestionsOnly();
            return;
        }

        this.panelTarget.classList.add('is-open');
        this.#showPopularAndProducts();
        this.#updateShowAllLinkHref();
        this.debounceFetch(query);
    }

    open() {
        if (this.hasPanelTarget) {
            this.panelTarget.classList.add('is-open');
        }
        this.#updateOverlayForSearch(true);
        if (!this.currentQuery) {
            this.#showSuggestionsOnly();
        } else {
            this.#showPopularAndProducts();
        }
    }

    onFocus() {
        if (!this.hasPanelTarget) {
            return;
        }

        this.panelTarget.classList.add('is-open');
        this.#updateOverlayForSearch(true);
        if (!this.currentQuery) {
            this.#showSuggestionsOnly();
        } else {
            this.#showPopularAndProducts();
        }
    }

    openSearchFromMobile(event) {
        if (event) {
            event.preventDefault();
        }

        if (this.hasPanelTarget) {
            if (this.panelTarget.classList.contains('is-open')) {
                this.close();
            } else {
                this.open();
                if (this.hasInputTarget) {
                    this.inputTarget.focus();
                }
            }
        }
    }

    openSearchFromLegacyInput(event) {

        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }

        if (this.hasInputTarget) {
            this.currentQuery = (this.inputTarget.value || '').trim();
        }

        this.open();

        if (this.hasInputTarget) {
            this.inputTarget.focus();
        }
    }

    onBlur() {
        if (!this.hasPanelTarget) {
            return;
        }

        this.panelTarget.classList.remove('is-open');
    }

    close() {
        if (this.hasInputTarget) {
            this.inputTarget.value = '';
            this.inputTarget.blur();
        }
        if (typeof document.activeElement !== 'undefined' && document.activeElement) {
            document.activeElement.blur();
        }

        this.currentQuery = '';
        if (this.hasPanelTarget) {
            this.panelTarget.classList.remove('is-open');
        }

        this.#updateOverlayForSearch(false);
        this.#showSuggestionsOnly();
    }

    #updateOverlayForSearch(isOpen) {
        if (!window.jQuery) {
            return;
        }

        const overlay = window.jQuery('.overlay-header');
        if (!overlay.length) {
            return;
        }

        if (isOpen) {
            overlay.stop(true, true).fadeIn(300);
            return;
        }

        const langModalOpen = window.jQuery('.header-navigation').hasClass('lang-modal-open');
        if (!langModalOpen) {
            overlay.stop(true, true).fadeOut(300);
        }
    }

    handleClickOutside(event) {
        if (!this.hasPanelTarget) {
            return;
        }

        const inLegacySearch = event.target.closest && event.target.closest('.menu.search');
        if (inLegacySearch) {
            return;
        }

        if (!this.panelTarget.contains(event.target)) {
            this.close();
        }
    }

    clearResults() {
        if (this.hasPopularListTarget) {
            this.popularListTarget.replaceChildren();
        }
        if (this.hasProductsWrapperTarget) {
            this.productsWrapperTarget.classList.add('is-empty');
        }
        if (this.hasProductsSliderTarget) {
            this.productsSliderTarget.replaceChildren();
        }
    }

    fetchInitialSuggestions() {
        if (this.suggestionsLoaded) {
            return;
        }

        const endpoint = this.element.dataset.headerOldRaptorAutocompleteEndpointValue || '';
        const apiKey = this.element.dataset.headerOldRaptorApiKeyValue || '';
        const indexes = (this.element.dataset.headerOldRaptorIndexesValue || '')
            .split(',')
            .map(s => s.trim())
            .filter(Boolean);

        if (!endpoint || !apiKey || !indexes.length) {
            return;
        }

        const productIndex = this.element.dataset.headerOldRaptorProductIndexValue || '';
        const blogIndex = this.element.dataset.headerOldRaptorBlogIndexValue || '';
        const newsIndex = this.element.dataset.headerOldRaptorNewsIndexValue || '';
        const categoriesIndex = this.element.dataset.headerOldRaptorCategoriesIndexValue || '';

        const searches = indexes.map(collection => this.#buildRaptorSearchParams(collection, productIndex, blogIndex, newsIndex, categoriesIndex, '*', 2));

        fetch(endpoint, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-TYPESENSE-API-KEY': apiKey,
                'Accept': 'application/json',
            },
            body: JSON.stringify({ searches }),
        })
            .then(res => (res.ok ? res.json() : null))
            .then(data => {
                const results = (data && Array.isArray(data.results)) ? data.results : [];
                const seen = new Set();
                const names = [];
                results.forEach(r => {
                    const hits = Array.isArray(r.hits) ? r.hits : [];
                    hits.forEach(hit => {
                        const doc = hit.document || {};
                        const name = doc.name || doc.label || '';
                        if (name && !seen.has(name)) {
                            seen.add(name);
                            names.push(name);
                        }
                    });
                });
                this.renderSuggestions(names);
                this.suggestionsLoaded = true;
            })
            .catch(() => {
                // fail silently
            });
    }

    renderSuggestions(names) {
        if (!this.hasSuggestionsListTarget) {
            return;
        }
        const items = Array.isArray(names) ? names : [];
        const baseUrl = this.searchPageUrlValue || this.element.dataset.headerOldSearchPageUrlValue || '';
        const fragment = document.createDocumentFragment();
        items.forEach((name) => {
            if (!name) return;
            const link = document.createElement('a');
            link.className = 'header-search-suggestion';
            link.href = baseUrl ? `${baseUrl}?term=${encodeURIComponent(name)}` : '#';
            link.textContent = name;
            fragment.appendChild(link);
        });
        this.suggestionsListTarget.replaceChildren(fragment);
    }

    #showSuggestionsOnly() {
        if (this.hasSuggestionsSectionTarget) {
            this.suggestionsSectionTarget.classList.remove('d-none');
        }
        if (this.hasPopularSectionTarget) {
            this.popularSectionTarget.classList.add('d-none');
        }
        if (this.hasProductsSectionTarget) {
            this.productsSectionTarget.classList.add('d-none');
        }
    }

    #showPopularAndProducts() {
        if (this.hasSuggestionsSectionTarget) {
            this.suggestionsSectionTarget.classList.add('d-none');
        }
        if (this.hasPopularSectionTarget) {
            this.popularSectionTarget.classList.remove('d-none');
        }
        if (this.hasProductsSectionTarget) {
            this.productsSectionTarget.classList.remove('d-none');
        }
    }

    debounceFetch(query) {
        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer);
        }

        this.debounceTimer = setTimeout(() => {
            this.fetchAutocomplete(query);
        }, 250);
    }

    fetchAutocomplete(query) {
        const endpoint = this.element.dataset.headerOldRaptorAutocompleteEndpointValue || '';
        const apiKey = this.element.dataset.headerOldRaptorApiKeyValue || '';
        const productIndex = this.element.dataset.headerOldRaptorProductIndexValue || '';
        const blogIndex = this.element.dataset.headerOldRaptorBlogIndexValue || '';
        const newsIndex = this.element.dataset.headerOldRaptorNewsIndexValue || '';
        const categoriesIndex = this.element.dataset.headerOldRaptorCategoriesIndexValue || '';
        const indexes = (this.element.dataset.headerOldRaptorIndexesValue || '')
            .split(',')
            .map(s => s.trim())
            .filter(Boolean);

        if (!endpoint || !apiKey || !indexes.length) {
            return;
        }

        const primarySearches = indexes.map(collection => this.#buildRaptorSearchParams(collection, productIndex, blogIndex, newsIndex, categoriesIndex, query, 9));

        const headers = {
            'Content-Type': 'application/json',
            'X-TYPESENSE-API-KEY': apiKey,
            'Accept': 'application/json',
        };

        const doSearch = (searches) =>
            fetch(endpoint, {
                method: 'POST',
                headers,
                body: JSON.stringify({ searches }),
            }).then(res => {
                if (!res.ok) {
                    throw new Error('Raptor multi_search failed');
                }
                return res.json();
            });

        const collectFromResults = (results, { existingPopular, existingProducts, searchList } = {}) => {
            const popular = existingPopular || [];
            const products = existingProducts || [];
            const seenTerms = new Set(popular);
            const list = searchList || primarySearches;
            const seenProductKeys = new Set(
                products.map(p => this.#productDedupeKeyFromProduct(p)).filter(Boolean),
            );

            (results || []).forEach((r, idx) => {
                const collection = list[idx] && list[idx].collection;
                const hits = Array.isArray(r.hits) ? r.hits : [];
                hits.forEach(hit => {
                    const doc = hit.document || {};
                    const highlight = hit.highlight || {};
                    const label = highlight.name?.snippet || doc.name || doc.label || '';
                    if (label && popular.length < 5 && !seenTerms.has(label)) {
                        seenTerms.add(label);
                        popular.push(label);
                    }
                    if (productIndex && collection === productIndex && products.length < 9) {
                        const key = this.#productDedupeKeyFromDocument(doc);
                        if (key && seenProductKeys.has(key)) {
                            return;
                        }
                        if (key) {
                            seenProductKeys.add(key);
                        }
                        products.push({
                            id: doc.id != null && doc.id !== '' ? doc.id : undefined,
                            image: doc.image || '',
                            category_tag: doc.category_tag || '',
                            name: doc.name || '',
                            link: doc.link || '#',
                        });
                    }
                });
            });

            return { popular, products };
        };

        doSearch(primarySearches)
            .then(data => {
                const results = Array.isArray(data.results) ? data.results : [];
                let { popular, products } = collectFromResults(results, {
                    existingPopular: [],
                    existingProducts: [],
                });

                const needsPopularFallback = popular.length < 5;
                const needsProductFallback = products.length < 5;

                if (!needsPopularFallback && !needsProductFallback) {
                    this.renderPopular(popular);
                    this.renderProducts(products, null);
                    return;
                }

                const fallbackSearches = [];
                if (needsPopularFallback) {
                    fallbackSearches.push(
                        ...indexes.map(collection => this.#buildRaptorSearchParams(collection, productIndex, blogIndex, newsIndex, categoriesIndex, '*', 20)),
                    );
                }
                if (needsProductFallback && productIndex) {
                    fallbackSearches.push(this.#buildRaptorSearchParams(productIndex, productIndex, blogIndex, newsIndex, categoriesIndex, '*', 20));
                }

                if (!fallbackSearches.length) {
                    this.renderPopular(popular);
                    this.renderProducts(products, null);
                    return;
                }

                return doSearch(fallbackSearches).then(fallbackData => {
                    const fallbackResults = Array.isArray(fallbackData.results)
                        ? fallbackData.results
                        : [];
                    const merged = collectFromResults(fallbackResults, {
                        existingPopular: popular,
                        existingProducts: products,
                        searchList: fallbackSearches,
                    });
                    popular = merged.popular.slice(0, 5);
                    products = merged.products.slice(0, 9);

                    this.renderPopular(popular);
                    this.renderProducts(products, null);
                });
            })
            .catch(() => {
                // fail silently in header autocomplete
            });
    }

    renderPopular(items) {
        if (!this.hasPopularListTarget) {
            return;
        }

        const terms = Array.isArray(items) ? items.slice(0, 5) : [];
        const fragment = document.createDocumentFragment();

        terms.forEach((term) => {
            const label = typeof term === 'string' ? term : (term && term.label) || '';
            if (!label) {
                return;
            }
            const li = document.createElement('li');
            li.className = 'header-search-popular-item';
            const link = document.createElement('a');
            link.className = 'header-search-popular-link';
            const termQuery = this.#stripMarkTags(label);
            const baseUrl = this.searchPageUrlValue || this.element.dataset.headerOldSearchPageUrlValue || '';
            link.href = baseUrl ? `${baseUrl}?term=${encodeURIComponent(termQuery)}` : '#';
            this.#appendPopularLabel(link, label);
            li.appendChild(link);
            fragment.appendChild(li);
        });

        this.popularListTarget.replaceChildren(fragment);
    }

    renderProducts(items, overviewUrl) {
        if (!this.hasProductsWrapperTarget || !this.hasProductsSliderTarget) {
            return;
        }

        const products = Array.isArray(items) ? items.slice(0, 9) : [];

        if (window.jQuery && window.jQuery.fn.slick) {
            const slickWrapper = window.jQuery(this.productsSliderTarget);
            if (slickWrapper.hasClass('slick-initialized')) {
                slickWrapper.slick('unslick');
            }
        }

        this.productsSliderTarget.replaceChildren();

        if (!products.length) {
            this.productsWrapperTarget.classList.add('is-empty');
            return;
        }

        const fragment = document.createDocumentFragment();
        const ctaLabel = this.productCtaLabelValue || 'Details';

        products.forEach((product) => {
            const imageSrc = this.#safeResourceUrl(product.image || '');
            const category = product.category_tag || '';
            const title = product.name || '';
            const href = this.#safeNavHref(product.link || '');

            const slide = document.createElement('div');
            slide.className = 'header-search-product-teaser';

            const anchor = document.createElement('a');
            anchor.href = href;

            const inner = document.createElement('div');
            inner.className = 'header-search-product-teaser-inner';

            const imageWrap = document.createElement('div');
            imageWrap.className = 'header-search-product-image';
            if (imageSrc) {
                const img = document.createElement('img');
                img.src = imageSrc;
                img.alt = title;
                imageWrap.appendChild(img);
            }
            inner.appendChild(imageWrap);

            const meta = document.createElement('div');
            meta.className = 'header-search-product-meta';

            const titleEl = document.createElement('div');
            titleEl.className = 'header-search-product-title';
            titleEl.textContent = title;
            meta.appendChild(titleEl);

            const catEl = document.createElement('div');
            catEl.className = 'header-search-product-category';
            catEl.textContent = category;
            meta.appendChild(catEl);

            const ctaWrap = document.createElement('div');
            ctaWrap.className = 'header-search-product-cta';
            const ctaSpan = document.createElement('span');
            ctaSpan.className = 'btn btn-secondary';
            ctaSpan.appendChild(document.createTextNode(ctaLabel));
            const ctaIcon = document.createElement('i');
            ctaIcon.className = 'fal fa-angle-right';
            ctaSpan.appendChild(ctaIcon);
            ctaWrap.appendChild(ctaSpan);
            meta.appendChild(ctaWrap);

            inner.appendChild(meta);
            anchor.appendChild(inner);
            slide.appendChild(anchor);
            fragment.appendChild(slide);
        });

        this.productsSliderTarget.replaceChildren(fragment);
        this.productsWrapperTarget.classList.remove('is-empty');

        this.#updateShowAllLinkHref();

        const slickOptions = {
            variableWidth: false,
            slidesToShow: 3.3,
            slidesToScroll: 1,
            dots: true,
            arrows: true,
            infinite: false,
            autoplay: false,
            adaptiveHeight: false,
            prevArrow: "<span class='slick-prev pull-left'><i class='fal fa-chevron-left'></i></span>",
            nextArrow: "<span class='slick-next pull-right'><i class='fal fa-chevron-right'></i></span>",
            responsive: [
                { breakpoint: 1200, settings: { variableWidth: false, slidesToShow: 3.5 } },
                { breakpoint: 768, settings: { variableWidth: false, slidesToShow: 2 } },
            ],
        };
        if (window.jQuery && window.jQuery.fn.slick) {
            window.jQuery(this.productsSliderTarget).slick(slickOptions);
        }
    }

    /**
     * Typesense query_by fields for the product index (from app.raptor_search.product_query_by).
     *
     * @returns {string}
     */
    #productQueryBy() {
        const configured = (this.raptorProductQueryByValue || '').trim();
        return configured || 'name,description,alloy';
    }

    /**
     * @param {string} collection
     * @param {string} productIndex
     * @param {string} blogIndex
     * @param {string} newsIndex
     * @param {string} categoriesIndex
     * @param {string} query
     * @param {number} perPage
     * @returns {{collection: string, q: string, query_by: string, per_page: number, filter_by?: string}}
     */
    #buildRaptorSearchParams(collection, productIndex, blogIndex, newsIndex, categoriesIndex, query, perPage) {
        const params = {
            collection,
            q: query,
            query_by: productIndex && collection === productIndex ? this.#productQueryBy() : 'name',
            per_page: perPage,
        };
        const marketFilter = (this.raptorMarketFilterValue || '').trim();
        const pagesIndex = (this.raptorPagesIndexValue || '').trim();
        const isMarketScopedIndex = (productIndex && collection === productIndex)
            || (blogIndex && collection === blogIndex)
            || (newsIndex && collection === newsIndex)
            || (categoriesIndex && collection === categoriesIndex)
            || (pagesIndex && collection === pagesIndex);
        if (marketFilter && isMarketScopedIndex) {
            params.filter_by = `market:${marketFilter}`;
        }
        return params;
    }

    /**
     * Stable key for deduping header search product previews across primary + fallback Typesense queries.
     *
     * @param {Record<string, unknown>} doc Typesense hit.document
     * @returns {string}
     */
    #productDedupeKeyFromDocument(doc) {
        if (!doc || typeof doc !== 'object') {
            return '';
        }
        const id = doc.id != null ? String(doc.id).trim() : '';
        if (id) {
            return `id:${id}`;
        }
        const link = typeof doc.link === 'string' ? doc.link.trim() : '';
        if (link && link !== '#') {
            return `link:${link}`;
        }
        const name = typeof doc.name === 'string' ? doc.name.trim() : '';
        if (name) {
            return `name:${name}`;
        }
        return '';
    }

    /**
     * Same semantics as {@link #productDedupeKeyFromDocument} for rendered preview items (merged passes).
     *
     * @param {{ id?: unknown, link?: string, name?: string }} product
     * @returns {string}
     */
    #productDedupeKeyFromProduct(product) {
        if (!product || typeof product !== 'object') {
            return '';
        }
        if (product.id != null && String(product.id).trim() !== '') {
            return `id:${String(product.id).trim()}`;
        }
        const link = typeof product.link === 'string' ? product.link.trim() : '';
        if (link && link !== '#') {
            return `link:${link}`;
        }
        const name = typeof product.name === 'string' ? product.name.trim() : '';
        if (name) {
            return `name:${name}`;
        }
        return '';
    }

    #safeNavHref(url) {
        if (!url || typeof url !== 'string') {
            return '#';
        }
        const rewritten = rewriteRaptorTeaserLink(url.trim(), {
            marketFilter: this.raptorMarketFilterValue || '',
            usUrlPrefix: this.usUrlPrefixValue ?? '',
        });
        const t = typeof rewritten === 'string' ? rewritten.trim() : '';
        if (t.startsWith('/') && !t.startsWith('//')) {
            return t;
        }
        if (t.startsWith('#')) {
            return t;
        }
        try {
            const u = new URL(t, window.location.href);
            if (u.protocol === 'http:' || u.protocol === 'https:') {
                return u.href;
            }
        } catch {
            // ignore invalid URL
        }
        return '#';
    }

    #safeResourceUrl(url) {
        if (!url || typeof url !== 'string') {
            return '';
        }
        const t = url.trim();
        if (t.startsWith('/') && !t.startsWith('//')) {
            return t;
        }
        try {
            const u = new URL(t, window.location.href);
            if (u.protocol === 'http:' || u.protocol === 'https:') {
                return u.href;
            }
        } catch {
            // ignore invalid URL
        }
        return '';
    }

    #stripMarkTags(label) {
        if (typeof label !== 'string') {
            return '';
        }
        const normalized = this.#normalizePopularLabel(label);
        return normalized.replace(/<\/?mark\b[^>]*>/gi, '');
    }

    #appendPopularLabel(linkEl, label) {
        if (!linkEl) {
            return;
        }
        if (typeof label !== 'string') {
            linkEl.textContent = '';
            return;
        }

        const normalized = this.#normalizePopularLabel(label);
        const markPattern = /<mark\b[^>]*>(.*?)<\/mark>/gis;
        let lastIndex = 0;
        let match;

        while ((match = markPattern.exec(normalized)) !== null) {
            if (match.index > lastIndex) {
                linkEl.appendChild(document.createTextNode(normalized.slice(lastIndex, match.index)));
            }
            const markEl = document.createElement('mark');
            markEl.textContent = match[1];
            linkEl.appendChild(markEl);
            lastIndex = match.index + match[0].length;
        }

        if (lastIndex < normalized.length) {
            linkEl.appendChild(document.createTextNode(normalized.slice(lastIndex)));
        }
    }

    #normalizePopularLabel(label) {
        const decoded = this.#decodeHtmlEntities(label);
        return decoded
            .replace(/&lt;\s*mark\b/gi, '<mark')
            .replace(/&lt;\s*\/\s*mark\s*&gt;/gi, '</mark>')
            .replace(/<\s*mark\b/gi, '<mark')
            .replace(/<\s*\/\s*mark\s*>/gi, '</mark>');
    }

    #decodeHtmlEntities(text) {
        if (typeof text !== 'string' || !text.includes('&')) {
            return text || '';
        }
        return text.replace(/&(#x?[0-9a-f]+|amp|lt|gt|quot|apos|#39);/gi, (entity, token) => {
            const lower = token.toLowerCase();
            if (lower === 'amp') return '&';
            if (lower === 'lt') return '<';
            if (lower === 'gt') return '>';
            if (lower === 'quot') return '"';
            if (lower === 'apos' || lower === '#39') return '\'';
            if (lower.startsWith('#x')) {
                const code = Number.parseInt(lower.slice(2), 16);
                return Number.isNaN(code) ? entity : String.fromCodePoint(code);
            }
            if (lower.startsWith('#')) {
                const code = Number.parseInt(lower.slice(1), 10);
                return Number.isNaN(code) ? entity : String.fromCodePoint(code);
            }
            return entity;
        });
    }

    #updateShowAllLinkHref() {
        if (!this.hasShowAllLinkTarget) return;
        const base = this.productsSearchPageUrlValue || '';
        const term = (this.currentQuery || '').trim();
        this.showAllLinkTarget.href = term ? `${base}?term=${encodeURIComponent(term)}` : base || '#';
    }

    onSubmit(event) {
        event.preventDefault();

        const baseUrl = this.searchPageUrlValue || this.element.dataset.headerOldSearchPageUrlValue || '';
        if (!baseUrl || !this.hasInputTarget) {
            return;
        }

        const term = (this.inputTarget.value || '').trim();
        const url = term ? `${baseUrl}?term=${encodeURIComponent(term)}` : baseUrl;
        window.location.href = url;
    }

    #hasScrolled() {
        const scrollTop = window.scrollY || 0;
        const headerEl = this.hasHeaderTarget ? this.headerTarget : null;
        if (!headerEl) {
            this.lastScrollTop = scrollTop;
            return;
        }

        const headerNav = document.querySelector('.header-navigation');
        const checkWindowWidth = headerNav?.classList.contains('show')
            ? window.innerWidth >= 1350
            : true;

        const navbarHeight = headerEl.offsetHeight;

        const isScrollingDown = scrollTop > this.lastScrollTop;
        const isScrollingUp = scrollTop < this.lastScrollTop;

        if (isScrollingDown && scrollTop > navbarHeight && checkWindowWidth) {
            headerEl.classList.remove('nav-down', 'nav-down-active');
            headerEl.classList.add('nav-up');
        }

        if (isScrollingUp && checkWindowWidth) {
            headerEl.classList.remove('nav-up');

            if (scrollTop === 0) {
                headerEl.classList.add('nav-down');
                headerEl.classList.remove('nav-down-active');
            } else {
                headerEl.classList.add('nav-down-active');
                headerEl.classList.remove('nav-down');
            }
        }

        this.lastScrollTop = scrollTop;
    }
}

